/**
 * Video URL parsing and utilities for YouTube (and future Bilibili) support
 */
import { apiConfig } from '../lib/apiConfig';

// Python server URL for transcript extraction
export const REEDLE_PY_SERVER_URL = apiConfig.renderPyUrl;

/**
 * Parse a URL to detect if it's a video URL
 * @param {string} url - URL to parse
 * @returns {{ platform: 'youtube' | 'bilibili' | null, videoId: string | null }}
 */
export function parseVideoUrl(url) {
  if (!url) return { platform: null, videoId: null };
  
  const trimmedUrl = url.trim();
  
  // YouTube patterns:
  // - https://www.youtube.com/watch?v=VIDEO_ID
  // - https://youtu.be/VIDEO_ID
  // - https://www.youtube.com/embed/VIDEO_ID
  // - https://m.youtube.com/watch?v=VIDEO_ID
  const youtubePatterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/v\/)([a-zA-Z0-9_-]{11})/,
  ];
  
  for (const pattern of youtubePatterns) {
    const match = trimmedUrl.match(pattern);
    if (match) {
      return { platform: 'youtube', videoId: match[1] };
    }
  }
  
  // Bilibili patterns:
  // - https://www.bilibili.com/video/BV1xx...
  // - https://bilibili.com/video/BV1xx...
  // - https://m.bilibili.com/video/BV1xx...
  // - https://www.bilibili.com/video/BV1xx?p=2 (multi-part)
  // - https://www.bilibili.com/video/av12345
  // - https://b23.tv/xxx (short URL - needs redirect, but we can detect it)
  // - Direct BV number: BV1bW411n7fY

  // Direct BV number (not a URL)
  if (/^BV[a-zA-Z0-9]{10}$/.test(trimmedUrl)) {
    return { platform: 'bilibili', videoId: trimmedUrl };
  }

  // BV number in URL (various domains)
  const bvMatch = trimmedUrl.match(/bilibili\.com\/video\/(BV[a-zA-Z0-9]+)/i);
  if (bvMatch) {
    return { platform: 'bilibili', videoId: bvMatch[1] };
  }

  // AV number in URL (legacy format)
  const avMatch = trimmedUrl.match(/bilibili\.com\/video\/av(\d+)/i);
  if (avMatch) {
    return { platform: 'bilibili', videoId: `av${avMatch[1]}` };
  }

  // b23.tv short URL - extract the ID part (backend will handle redirect)
  const b23Match = trimmedUrl.match(/b23\.tv\/([a-zA-Z0-9]+)/);
  if (b23Match) {
    // Return the short code - backend needs to resolve it
    return { platform: 'bilibili', videoId: b23Match[1], isShortUrl: true };
  }
  
  return { platform: null, videoId: null };
}

/**
 * Check if URL is a YouTube URL
 */
export function isYouTubeUrl(url) {
  const { platform } = parseVideoUrl(url);
  return platform === 'youtube';
}

/**
 * Check if URL is a Bilibili URL
 */
export function isBilibiliUrl(url) {
  const { platform } = parseVideoUrl(url);
  return platform === 'bilibili';
}

/**
 * Get YouTube video thumbnail URL
 * @param {string} videoId - YouTube video ID
 * @param {string} quality - 'default' | 'mq' | 'hq' | 'sd' | 'maxres'
 * @returns {string} Thumbnail URL
 */
export function getYouTubeThumbnail(videoId, quality = 'hq') {
  const qualityMap = {
    default: 'default',
    mq: 'mqdefault',
    hq: 'hqdefault',
    sd: 'sddefault',
    maxres: 'maxresdefault',
  };
  return `https://img.youtube.com/vi/${videoId}/${qualityMap[quality] || 'hqdefault'}.jpg`;
}

/**
 * Fetch available transcripts for a YouTube video
 * @param {string} videoId - YouTube video ID
 * @returns {Promise<{ video_id: string, available_transcripts: Array }>}
 */
export async function fetchTranscriptList(videoId) {
  const response = await fetch(`${REEDLE_PY_SERVER_URL}/api/transcript/list`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ video_id: videoId }),
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to list transcripts');
  }
  
  return response.json();
}

/**
 * Fetch transcript content for a YouTube video
 * @param {string} videoId - YouTube video ID
 * @param {string|null} languageCode - Preferred language code (null = auto-select)
 * @param {boolean|null} isGenerated - Whether to fetch auto-generated transcript (to distinguish from manual)
 * @returns {Promise<{ video_id, language, language_code, is_generated, content, snippets }>}
 */
export async function fetchTranscript(videoId, languageCode = null, isGenerated = null) {
  const body = { video_id: videoId };
  if (languageCode) body.language_code = languageCode;
  if (isGenerated !== null) body.is_generated = isGenerated;

  const response = await fetch(`${REEDLE_PY_SERVER_URL}/api/transcript/fetch`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to fetch transcript');
  }

  return response.json();
}

/**
 * Parse timecode from transcript block text
 * @param {string} text - Block text like "[1:23] Some content"
 * @returns {{ seconds: number | null, text: string }}
 */
export function parseTimecode(text) {
  const match = text.match(/^\[(\d+):(\d{2})\]\s*(.*)/s);
  if (match) {
    const minutes = parseInt(match[1], 10);
    const seconds = parseInt(match[2], 10);
    return {
      seconds: minutes * 60 + seconds,
      text: match[3],
    };
  }
  return { seconds: null, text };
}

/**
 * Format seconds to timecode string [MM:SS]
 */
export function formatTimecode(totalSeconds) {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = Math.floor(totalSeconds % 60);
  return `[${minutes}:${seconds.toString().padStart(2, '0')}]`;
}

